import React, { useEffect, useRef, useState } from "react";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import "./editEventModal.css";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import CloseIcon from "@mui/icons-material/Close";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_ALL_USERS, GET_CLIENT_BY_ID, GET_CALENDAR_EVENT_FOR_DOC, GET_ZONE_WEATHER_FOR_RANGE } from "../../../../utils/GraphQL/queries";
import WeatherChip from "../../../../components/WeatherChip/WeatherChip";
import { buildWeatherLookup, weatherForJob } from "../../../../utils/weatherZoneJoin";
import {
  UPDATE_CALENDAR_ITEM,
  SET_CALENDAR_ITEM_AVAILABLE_SOONER,
  DELETE_CALENDAR_ITEM,
  UPDATE_QUOTE_INVOICE,
  BREAK_CALENDAR_CHAIN,
  JOIN_CALENDAR_CHAIN,
  PROMOTE_PLACEHOLDER_TO_DOC,
} from "../../../../utils/GraphQL/mutations";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";
import ChainSelector from "../ChainSelector/ChainSelector";
import PlaceholderCleanupModal from "../PlaceholderCleanupModal/PlaceholderCleanupModal";
import PayPeriodMoveConfirmModal, { isNeedsPeriodConfirm } from "../PayPeriodMoveConfirmModal/PayPeriodMoveConfirmModal";
import BdrInput from "../../../../components/BdrInput/BdrInput";

import NumberStepper from "../NumberStepper/NumberStepper";
import { getSplitBetween, buildSplitBetween } from "../../../../utils/calendarSplitBetween";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { extractCalNotes } from "../../../../utils/formatters/extractCalNotes";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { hasAnyRole } from '../../../../utils/permissions';
import {
  PERSONAL_LABEL_MAX,
  toLocalNoonMsStr,
  repeatStateFrom,
  repeatInputFrom,
  repeatSaveWarning,
  isSeriesOccurrence,
  hasRepeatRule,
  isSameLocalDay,
  overrideCount,
} from "../../../../utils/calendarPersonalEvents";
import RepeatFields from "../shared/RepeatFields/RepeatFields";
import RepeatScopeModal from "../shared/RepeatScopeModal/RepeatScopeModal";
import SpecialRefinisherPayBlock, {
  buildSpecialRefinisherPayInput,
  validateSpecialRefinisherPay,
} from "../../../../components/SpecialRefinisherPayBlock/SpecialRefinisherPayBlock";
import {
  WEEKDAY_LABELS,
  generateDayList,
  buildWorkDaysPayload,
  previewDayTimes,
  seedCheckedKeysFromWorkDays,
} from "../../../../utils/workDayBuilder";

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;

const EditEventModal = ({ open, onClose, onEventUpdated, item }) => {
  const { handleSnack } = useSnackbar();
  // item shape: { calendarItem, customer, invoice, assignedUser }
  const [startMs, setStartMs] = useState("");
  const [endMs, setEndMs] = useState("");
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [priorityNote, setPriorityNote] = useState("");
  const [itemStatus, setItemStatus] = useState("");
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);
  const [splitMin, setSplitMin] = useState("");
  const [splitMax, setSplitMax] = useState("");
  const [srpEnabled, setSrpEnabled] = useState(false);
  const [srpAmount, setSrpAmount] = useState(null);
  const [srpPayType, setSrpPayType] = useState(null);
  // Dirty-gate: only send specialRefinisherPay when the user touched the SRP
  // block THIS session. Omitting the field preserves the server's stored
  // value (updateCalendarItem strips undefined), so a stale schedule-range
  // snapshot can no longer wipe an override on an unrelated save.
  const srpDirtyRef = useRef(false);
  // Calendar-event-types Phase 3a: editable note for Go By events.
  const [note, setNote] = useState("");
  // Personal ("SIX") events, 2026-09-11: editable label + All Day switch.
  const [label, setLabel] = useState("");
  // Repeat rule + the "which occurrences?" prompt (2026-09-20).
  const [repeat, setRepeat] = useState({
    on: false,
    freq: "weekly",
    interval: "1",
    endsOn: "",
  });
  const [scopePrompt, setScopePrompt] = useState(null);
  const [allDay, setAllDay] = useState(true);
  // Chain state (Phase 6). `existingChainGroupId` mirrors server state so the
  // Break Chain button knows when to show; `chainAfter` is the pending target
  // chosen from the selector dropdown, resolved at save time.
  const [existingChainGroupId, setExistingChainGroupId] = useState(null);
  const [chainAfter, setChainAfter] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clientNotes, setClientNotes] = useState([]);
  const [gateCode, setGateCode] = useState("");
  // Weather-by-zone (2026-06-22): the client's zone + the forecast for the
  // event's start day, shown as a forewarning chip on the read-only Client row.
  const [clientZone, setClientZone] = useState(null);
  const [zoneWeather, setZoneWeather] = useState(null);
  // Attach-by-edit (placeholders only): pick an invoice/quote to upgrade a
  // tentative placeholder into a real booking. Doc lists come from the client.
  const [selectedDocId, setSelectedDocId] = useState("");
  const [selectedDocType, setSelectedDocType] = useState(""); // 'invoice' | 'quote'
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  // Cleanup-modal handoff after an attach upgrades a placeholder (offers to
  // clear the customer's OTHER tentative placeholders).
  const [cleanupOpen, setCleanupOpen] = useState(false);
  // Promote-confirm: when the picked doc already has a calendar event, hold its
  // details so the confirmation modal can warn about the destructive move.
  const [promoteConfirm, setPromoteConfirm] = useState(null);
  // Pay-period move confirm (Option 2): { oldMs, newMs, onConfirm } when a move
  // would re-bucket an approved job's paycheck across a settled period.
  const [periodMove, setPeriodMove] = useState(null);
  // Captures the existing event's date during a promote so the period modal can
  // show old→new if the promote's move trips the pay-period guard.
  const promoteExistingStartMsRef = useRef(null);
  // Multi-day work-day checklist state. On open we seed checkedKeys from
  // the record's existing workDays (if any) so the prior selection is
  // preserved; if the record predates multi-day, we fall back to the
  // Sat/Sun auto-skip default for the generated date span.
  const [workDayList, setWorkDayList] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState(() => new Set());
  // Tracks the last startMs|endMs the reactive effect processed. The open
  // effect stamps this after seeding so the reactive effect doesn't clobber
  // the seeded checkedKeys (which could un-skip a weekday the user had
  // intentionally excluded on the stored record).
  const lastReactiveKeyRef = useRef("");

  const [getAllUsers] = useLazyQuery(GET_ALL_USERS);
  const [getClientById] = useLazyQuery(GET_CLIENT_BY_ID);
  const [getZoneWeather] = useLazyQuery(GET_ZONE_WEATHER_FOR_RANGE);
  const [updateCalendarItem] = useMutation(UPDATE_CALENDAR_ITEM);
  const [setAvailSooner] = useMutation(SET_CALENDAR_ITEM_AVAILABLE_SOONER);
  /*
   * Available Sooner. No reset-on-type-change here, unlike the create modal:
   * eventType is immutable post-create (L10), so the eligible/ineligible
   * decision is fixed for the life of this dialog.
   */
  const [availableSooner, setAvailableSooner] = useState(false);
  const [availSoonerAtOpen, setAvailSoonerAtOpen] = useState(false);
  const [deleteCalendarItem] = useMutation(DELETE_CALENDAR_ITEM);
  const [updateQuoteInvoice] = useMutation(UPDATE_QUOTE_INVOICE);
  const [breakCalendarChain] = useMutation(BREAK_CALENDAR_CHAIN);
  const [joinCalendarChain] = useMutation(JOIN_CALENDAR_CHAIN);
  const [getCalendarEventForDoc] = useLazyQuery(GET_CALENDAR_EVENT_FOR_DOC);
  const [promotePlaceholderToDoc] = useMutation(PROMOTE_PLACEHOLDER_TO_DOC);

  // Populate form from item when opened. MUI Dialog (default keepMounted=false)
  // tears down the portal on close but EditEventModal itself stays mounted, so
  // startMs/endMs persist between opens. We therefore seed workDayList and
  // checkedKeys directly here rather than relying on the reactive [startMs,
  // endMs] effect — otherwise re-opening the modal for a record whose times
  // match the previous session would hit React's Object.is bail-out on
  // setStartMs/setEndMs, the reactive effect would never re-fire, workDayList
  // would stay [], and a no-change save would collapse a multi-day event to
  // workDays: [] on the server.
  useEffect(() => {
    if (open && item?.calendarItem) {
      const ci = item.calendarItem;
      const nextStartMs = ci.startMs || "";
      const nextEndMs = ci.endMs || "";
      setStartMs(nextStartMs);
      setEndMs(nextEndMs);
      setAssignedUserIds(ci.assignedUserIds || []);
      setItemStatus(ci.itemStatus || "");
      setLocked(ci.locked || false);
      // Seeded BOTH ways: the live value drives the box, and the snapshot lets
      // save skip the mutation entirely when nobody touched it.
      setAvailableSooner(!!ci.availableSooner);
      setAvailSoonerAtOpen(!!ci.availableSooner);

      // Populate Min/Max: the doc's splitBetween (first NON-empty of invoice /
      // quote), or a tentative placeholder's own placeholderSplitBetween
      // (2026-10-29). utils/calendarSplitBetween.js is the one rule.
      const split = getSplitBetween(item) || [];
      setSplitMin(split[0] != null ? String(split[0]) : "");
      setSplitMax(split.length > 1 ? String(split[split.length - 1]) : "");

      // Populate specialRefinisherPay from calendar item (canonical for editing
      // back), falling back to linked invoice/quote if calendar item hasn't
      // received the propagated copy yet.
      const srp =
        ci.specialRefinisherPay?.enabled === true
          ? ci.specialRefinisherPay
          : item.invoice?.specialRefinisherPay?.enabled === true
            ? item.invoice.specialRefinisherPay
            : item.quote?.specialRefinisherPay?.enabled === true
              ? item.quote.specialRefinisherPay
              : ci.specialRefinisherPay; // null/disabled fall-through
      setSrpEnabled(!!srp?.enabled);
      setSrpAmount(srp?.amount || null);
      setSrpPayType(srp?.payType || null);
      // Re-seeding from the record = not a user edit.
      srpDirtyRef.current = false;

      // Join priority notes into a single editable string
      const notes = ci.priorityNotes || [];
      setPriorityNote(notes.filter(Boolean).join("\n"));

      // Seed the Go By note field from the record. Empty string when null so
      // the text input shows as empty rather than "null".
      setNote(ci.note || "");

      // Personal ("SIX") fields. Non-personal records carry label null /
      // isAllDay false, so these seed harmlessly for every type.
      setLabel(ci.label || "");
      setAllDay(ci.isAllDay === true);
      setRepeat(repeatStateFrom(ci));

      // Seed chain state from the record.
      setExistingChainGroupId(ci.chainGroupId || null);
      setChainAfter(null);

      setError("");
      setSubmitting(false);
      setDeleting(false);
      setClientNotes([]);
      setGateCode("");
      setSelectedDocId("");
      setSelectedDocType("");
      setInvoices([]);
      setQuotes([]);
      setCleanupOpen(false);

      // Seed the multi-day checklist synchronously from the item so a re-open
      // with unchanged times doesn't leave workDayList empty.
      const initialList = generateDayList(nextStartMs, nextEndMs);
      setWorkDayList(initialList);
      if (initialList.length > 0) {
        setCheckedKeys(seedCheckedKeysFromWorkDays(initialList, ci.workDays));
      } else {
        setCheckedKeys(new Set());
      }
      // Stamp the reactive effect's gate so its upcoming run for this same
      // span is a no-op — the open branch already did the work.
      lastReactiveKeyRef.current = `${nextStartMs}|${nextEndMs}`;

      fetchUsers();
      fetchClientNotes(ci.clientId);
    } else if (!open) {
      // Reset startMs/endMs too so a later re-open is guaranteed to trigger
      // the populate branch above (and the reactive effect below) even when
      // the next item happens to share the previous times.
      setStartMs("");
      setEndMs("");
      setWorkDayList([]);
      setCheckedKeys(new Set());
      lastReactiveKeyRef.current = "|";
      setClientZone(null);
      setZoneWeather(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  /*
   * A newly picked "is after" target belongs to the DAY it was picked on —
   * ChainSelector lists only that day's events. Moving the start to ANOTHER
   * day hid the pick but kept it in state, and save then joined this event to
   * a chain on a different day, where the server cascades the crew across the
   * whole chain. Same fix as CreateEventModal. The open branch above sets the
   * start and clears the target together, and a close empties the start, so
   * every session begins with the day unseeded. Local y/m/d, never a Duration.
   */
  const chainDayRef = useRef(null);
  useEffect(() => {
    const n = parseInt(startMs, 10);
    const d = Number.isFinite(n) ? new Date(n) : null;
    const day = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : null;
    if (chainDayRef.current !== null && day !== chainDayRef.current) {
      setChainAfter(null);
    }
    chainDayRef.current = day;
  }, [startMs]);

  // Weather forewarning: pull the client zone's forecast for the event's start
  // day when both are known. Re-runs when the user changes the start time.
  // Failure is swallowed (chip just doesn't render).
  useEffect(() => {
    if (!open || !clientZone || !startMs) {
      setZoneWeather(null);
      return undefined;
    }
    const ms = parseInt(startMs, 10);
    if (Number.isNaN(ms)) {
      setZoneWeather(null);
      return undefined;
    }
    const startWindow = String(ms - 86400000);
    const endWindow = String(ms + 86400000);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getZoneWeather({
          variables: { startMs: startWindow, endMs: endWindow },
          fetchPolicy: "cache-first",
        });
        if (!cancelled) setZoneWeather(data?.getZoneWeatherForRange || null);
      } catch (err) {
        console.error("Error fetching zone weather (edit event):", err);
        if (!cancelled) setZoneWeather(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientZone, startMs]);

  // Reactive regeneration when the user changes start/end times in the form.
  // Seeding from item.workDays is handled by the open effect above; here we
  // only preserve or Sat/Sun-default the user's checks against the new span.
  // Guarded by lastReactiveKeyRef so we don't re-run for the exact span the
  // open effect just seeded — otherwise the non-weekend default below would
  // re-check weekdays the user had intentionally excluded on the record.
  useEffect(() => {
    const key = `${startMs}|${endMs}`;
    if (key === lastReactiveKeyRef.current) return;
    lastReactiveKeyRef.current = key;
    const list = generateDayList(startMs, endMs);
    setWorkDayList(list);
    setCheckedKeys((prev) => {
      const next = new Set();
      for (const d of list) {
        if (prev.has(d.key)) {
          next.add(d.key);
        } else if (!d.isWeekend) {
          next.add(d.key);
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMs, endMs]);

  const fetchUsers = async () => {
    try {
      const { data } = await getAllUsers({ fetchPolicy: "network-only" });
      const allUsers = data?.getAllUsers || [];
      // Refinishers are the default field staff, so they should always appear
      // at the top of the assignee list. Other eligible users (admins) sort
      // below so they're still available when a non-refinisher needs to be
      // assigned to a job. Alphabetical within each bucket.
      const activeUsers = allUsers
        .filter((u) => u.active !== false && hasAnyRole(u.permission, ['refinisher', 'admin']))
        .sort((a, b) => {
          const aRef = hasAnyRole(a.permission, ['refinisher']) ? 0 : 1;
          const bRef = hasAnyRole(b.permission, ['refinisher']) ? 0 : 1;
          if (aRef !== bRef) return aRef - bRef;
          const aName = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          const bName = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          return aName.localeCompare(bName);
        });
      setUsers(activeUsers);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchClientNotes = async (cId) => {
    if (!cId) return;
    try {
      const { data } = await getClientById({
        variables: { clientId: cId, currentTime: Date.now().toString() },
        fetchPolicy: "network-only",
      });
      const client = data?.getClientById;
      if (client?.clientNotes) {
        setClientNotes(client.clientNotes);
      }
      setGateCode(client?.gateCode || "");
      setClientZone(client?.zone || null);
      // For the placeholder attach dropdown: unpaid invoices + all quotes,
      // newest first (mirrors CreateEventModal). Only rendered when the event
      // being edited is a tentative placeholder.
      const clientInvoices = (client?.invoices || [])
        .filter((inv) => !inv.invoicePaid)
        .sort(
          (a, b) => (parseInt(b.dateCreated) || 0) - (parseInt(a.dateCreated) || 0)
        );
      setInvoices(clientInvoices);
      const clientQuotes = [...(client?.quotes || [])].sort(
        (a, b) => (parseInt(b.dateCreated) || 0) - (parseInt(a.dateCreated) || 0)
      );
      setQuotes(clientQuotes);
    } catch (err) {
      console.error("Error fetching client notes:", err);
    }
  };

  // Invoices and quotes as one list with group labels. `header: true` rows
  // render but cannot be picked and the arrows step over them — what
  // `<option disabled>` did in the native list.
  const docOptions = [
    ...(invoices.length > 0 ? [{ header: true, label: "— Invoices —" }] : []),
    ...invoices.map((inv) => ({
      value: `invoice:${inv.id}`,
      label: `#${inv.invoiceNum} — ${priceStrFormatter(inv.invoiceTotal)}`,
    })),
    ...(quotes.length > 0 ? [{ header: true, label: "— Quotes —" }] : []),
    ...quotes.map((q) => ({
      value: `quote:${q.id}`,
      label: `#${q.quoteNum} — ${priceStrFormatter(q.quoteTotal)}`,
    })),
  ];

  const handleDocChange = (e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedDocId("");
      setSelectedDocType("");
      return;
    }
    const [type, ...idParts] = val.split(":");
    setSelectedDocId(idParts.join(":"));
    setSelectedDocType(type);
  };

  const getCustomerName = () => {
    const c = item?.customer;
    if (!c) return "Unknown";
    return `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unknown";
  };

  const getDocLabel = () => {
    const inv = item?.invoice;
    const q = item?.quote;
    if (inv) {
      return `Invoice #${inv.invoiceNum} — ${priceStrFormatter(inv.invoiceTotal)}`;
    }
    if (q) {
      return `Quote #${q.quoteNum} — ${priceStrFormatter(q.quoteTotal)}`;
    }
    return "N/A";
  };

  const handleStartTimeReturn = (mill) => {
    if (typeof mill !== "string") return;
    const newStart = parseInt(mill);
    if (isNaN(newStart)) return;

    setStartMs(mill);

    // If end time exists, sync the day and validate
    if (endMs) {
      const oldEnd = parseInt(endMs);
      if (!isNaN(oldEnd)) {
        // Move end time to same day as new start, keeping end's hours/minutes
        const startDt = new Date(newStart);
        const endDt = new Date(oldEnd);
        const syncedEnd = new Date(
          startDt.getFullYear(), startDt.getMonth(), startDt.getDate(),
          endDt.getHours(), endDt.getMinutes(), endDt.getSeconds()
        ).getTime();

        if (newStart >= syncedEnd) {
          // Start is after or equal to end — push end to start + 2hrs
          setEndMs(String(newStart + TWO_HOURS_MS));
        } else {
          setEndMs(String(syncedEnd));
        }
      }
    }
  };

  const handleEndTimeReturn = (mill) => {
    if (typeof mill !== "string") return;
    const newEnd = parseInt(mill);
    if (isNaN(newEnd)) return;

    if (startMs) {
      const curStart = parseInt(startMs);
      if (!isNaN(curStart) && newEnd <= curStart) {
        // End is before or equal to start — shift start to end - 1hr
        setStartMs(String(newEnd - ONE_HOUR_MS));
        setEndMs(mill);
        return;
      }
    }

    setEndMs(mill);
  };

  // Promote-in-place: perform the server promote, then hand off to the
  // "clear other placeholders" cleanup. confirmMove=true authorizes the
  // destructive move when the doc already had an event.
  const runPromote = async (confirmMove, confirmPeriod = false) => {
    const variables = { placeholderId: item.calendarItem.id };
    if (selectedDocType === "invoice") variables.invoiceId = selectedDocId;
    else variables.quoteId = selectedDocId;
    if (confirmMove) variables.confirmDocMove = true;
    if (confirmPeriod) variables.confirmPeriodMove = true;
    try {
      await promotePlaceholderToDoc({ variables });
      handleSnack("Promoted", 2500);
      setPromoteConfirm(null);
      setPeriodMove(null);
      setCleanupOpen(true); // offer to clear the customer's other placeholders
    } catch (err) {
      // Moving the existing approved event across a settled pay period — confirm.
      if (isNeedsPeriodConfirm(err)) {
        setPromoteConfirm(null);
        setPeriodMove({
          oldMs: promoteExistingStartMsRef.current,
          newMs: item.calendarItem.startMs,
          onConfirm: () => runPromote(true, true),
        });
        return; // modal takes over; keep submitting until it resolves
      }
      console.error("[EditEvent] promote failed:", err?.message || err);
      const clean = (err?.message || "")
        .replace(/^.*?NEEDS_CONFIRM:?\s*/i, "")
        .replace(/^Error:\s*/i, "");
      handleSnack(clean || "Promote failed", 3500);
      setPromoteConfirm(null);
      setSubmitting(false);
    }
  };

  // After the placeholder's field edits save, decide how to bind the doc:
  // if the doc already has a calendar event, open the confirmation modal
  // (the move is destructive); otherwise promote (attach) immediately.
  const beginPromote = async () => {
    try {
      const variables =
        selectedDocType === "invoice"
          ? { invoiceId: selectedDocId }
          : { quoteId: selectedDocId };
      const { data } = await getCalendarEventForDoc({
        variables,
        fetchPolicy: "network-only",
      });
      const existing = data?.getCalendarEventForDoc;
      if (existing?.calendarItem?.id) {
        promoteExistingStartMsRef.current = existing.calendarItem.startMs;
        setPromoteConfirm({
          existingStartMs: existing.calendarItem.startMs,
          placeholderStartMs: item.calendarItem.startMs,
          docLabel: existing.invoice
            ? `Invoice #${existing.invoice.invoiceNum}`
            : existing.quote
              ? `Quote #${existing.quote.quoteNum}`
              : "That document",
        });
        setSubmitting(false); // let the confirmation modal be interactive
      } else {
        await runPromote(false); // no existing event — attach in place
      }
    } catch (err) {
      console.error("[EditEvent] promote pre-check failed:", err?.message || err);
      handleSnack("Couldn't check the calendar — try again", 3000);
      setSubmitting(false);
    }
  };

  // Personal ("SIX") save — minimal whitelist input, mirroring the mobile
  // dialog: { id, label, startMs, isAllDay, endMs (timed only), note (ALWAYS —
  // "" clears) }. The server's personal update branch $unsets endMs when the
  // record turns all-day, so endMs is simply omitted here in that case.
  // `scope` is null for a one-off. For an occurrence of a repeating series the
  // caller has already been through RepeatScopeModal — the server REFUSES an
  // occurrence-id update that arrives without one, precisely so an older
  // client's intent is never invented for it.
  // Extracted so it can run BEFORE the scope prompt: asking "which events?"
  // and only then saying "please enter a label" makes the user answer the
  // question twice. Returns "" when the form is good.
  const validatePersonalForm = () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return "Please enter a label";
    if (trimmedLabel.length > PERSONAL_LABEL_MAX) {
      return `Label must be ${PERSONAL_LABEL_MAX} characters or fewer`;
    }
    if (!startMs) return allDay ? "Please select a date" : "Please select a start time";
    if (!allDay) {
      if (!endMs) return "Please select an end time";
      if (parseInt(endMs) <= parseInt(startMs)) return "End time must be after start time";
    }
    return "";
  };

  const handleSavePersonal = async (scope) => {
    const trimmedLabel = label.trim();
    const invalid = validatePersonalForm();
    if (invalid) {
      setError(invalid);
      if (invalid === "End time must be after start time") handleSnack(invalid, 3000);
      return;
    }

    setSubmitting(true);
    const input = {
      id: item.calendarItem.id,
      label: trimmedLabel,
      startMs: allDay ? toLocalNoonMsStr(startMs) : startMs,
      isAllDay: allDay,
      note: note.trim(),
    };
    if (!allDay) input.endMs = endMs;
    if (scope) input.scope = scope;
    // Only the client-settable half of the rule: the weekday / day-of-month
    // and the skipped days are server-owned, and a client that cannot name
    // them cannot clobber them.
    const recurrence = repeatInputFrom(repeat, {
      hadRule: hasRepeatRule(item),
    });
    if (recurrence) input.recurrence = recurrence;

    try {
      await updateCalendarItem({ variables: { input } });
      handleSnack("Personal Event Updated", 2500);
      onEventUpdated?.();
    } catch (err) {
      console.error("Error updating personal calendar item:", err);
      const msg = err.message || "Failed to update personal event";
      setError(msg);
      handleSnack(msg, 3000);
    }
    setSubmitting(false);
  };

  // periodConfirmed is only true when the pay-period confirm modal retries this
  // save. `=== true` guards against the Save button passing a click event arg.
  const handleSave = async (periodConfirmed) => {
    setError("");

    if ((item?.calendarItem?.eventType || "job") === "personal") {
      // An occurrence of a repeating series must say which occurrences the
      // edit applies to; a one-off saves straight through.
      if (isSeriesOccurrence(item)) {
        // Validate first — otherwise the user answers "which events?", the
        // prompt closes, and only then are they told the label is empty.
        const invalid = validatePersonalForm();
        if (invalid) {
          setError(invalid);
          return;
        }
        setScopePrompt({ mode: "edit" });
        return;
      }
      await handleSavePersonal(null);
      return;
    }

    if (!startMs) {
      setError("Start time is required");
      return;
    }

    // Final validation: end before start
    if (endMs && startMs && parseInt(endMs) < parseInt(startMs)) {
      setError("End time cannot be before start time");
      handleSnack("End time cannot be before start time", 3000);
      return;
    }

    // Build workDays payload from current checkbox state. If the date
    // span collapsed to a single day, workDayList will be empty and
    // workDays: [] signals single-day to the server.
    const checkedInOrder = workDayList.filter((d) => checkedKeys.has(d.key));
    const workDaysPayload = buildWorkDaysPayload(checkedInOrder, startMs, endMs);

    if (workDayList.length > 0 && checkedInOrder.length === 0) {
      setError("Select at least one work day");
      return;
    }

    setSubmitting(true);

    const notesArr = priorityNote
      .trim()
      .split("\n")
      .filter((n) => n.trim());

    // eventType is immutable post-create (L10) — do NOT include it in the
    // update input. Branch on it here for which optional fields to send.
    const eventType = item.calendarItem.eventType || "job";

    // Vacation validation: exactly one assigned refinisher.
    if (eventType === "vacation") {
      if (!Array.isArray(assignedUserIds) || assignedUserIds.length !== 1) {
        setError("Time Off must be tied to exactly one refinisher");
        setSubmitting(false);
        return;
      }
    }

    const input = {
      id: item.calendarItem.id,
      startMs,
      endMs: endMs || undefined,
      workDays: workDaysPayload,
      assignedUserIds,
      priorityNotes: notesArr,
      itemStatus: itemStatus || null,
      locked,
    };
    // Pay-period guard retry: only set when the confirm modal re-invoked save.
    if (periodConfirmed === true) input.confirmPeriodMove = true;

    // Placeholder Min/Max (2026-10-29): a placeholder has no doc, so the number
    // rides on the calendar item. ALWAYS sent for a placeholder so clearing
    // both steppers clears it ([] -> server $unset). Doc-bearing jobs keep
    // writing to the doc further down. When this save is also a promote, the
    // server carries the saved number onto the doc and drops it from the item.
    if (eventType === "job" && item.calendarItem.isPlaceholder) {
      input.placeholderSplitBetween = buildSplitBetween(splitMin, splitMax);
    }

    // Promote-in-place: a placeholder + a picked doc is handled via the
    // dedicated promotePlaceholderToDoc flow AFTER the field edits below save —
    // NOT by stuffing the doc into this update (which would hit the dup-guard
    // and can't safely move an existing event). The doc is intentionally left
    // out of `input` here.
    const attachingDoc =
      !!item.calendarItem.isPlaceholder && !!selectedDocId;

    if (eventType === "go_by" || eventType === "vacation") {
      // Empty string clears the note; non-empty persists it. UpdateCalendarItem
      // accepts the field as optional so omitting preserves server value.
      input.note = note.trim();
    }

    // Special Refinisher Pay override (real job events only — a placeholder has
    // no doc to attach pay to; SRP is set in a follow-up edit after attach).
    // Dirty-gated: only sent when the user touched the SRP block this session.
    // Omitting the field preserves the stored value server-side, so a stale
    // snapshot can't wipe an override set elsewhere on an unrelated save.
    if (
      eventType === "job" &&
      !item.calendarItem.isPlaceholder &&
      srpDirtyRef.current
    ) {
      const srpValidationError = validateSpecialRefinisherPay(srpEnabled, srpAmount);
      if (srpValidationError) {
        setError(srpValidationError);
        handleSnack(srpValidationError, 4000);
        setSubmitting(false);
        return;
      }
      input.specialRefinisherPay = buildSpecialRefinisherPayInput(
        srpEnabled,
        srpAmount,
        srpPayType
      );
    }

    // Chain join is deferred to AFTER the save (see joinCalendarChain block
    // below). Setting chainGroupId directly via updateCalendarItem tripped a
    // self-heal race: rebalance fires on the chainGroupId change, finds a
    // single-member group, and clears chainGroupId. The dedicated
    // `joinCalendarChain` mutation does both writes atomically.

    try {
      await updateCalendarItem({ variables: { input } });

      // Promote-in-place: the placeholder's field edits are now saved. Bind the
      // doc via the dedicated flow (which detects + confirms an existing event).
      // Skips the chain/split logic below — a placeholder being promoted doesn't
      // carry a split, and any existing invoiced event keeps its own.
      if (attachingDoc) {
        await beginPromote();
        return; // beginPromote owns the snack / cleanup / close lifecycle
      }

      // Chain join: atomic server-side mutation sets chainGroupId on BOTH
      // my event and the target in one updateMany, so rebalance never sees
      // a singleton group. Skipped for vacation (server rejects defensively).
      if (chainAfter?.targetId && eventType !== "vacation") {
        try {
          await joinCalendarChain({
            variables: {
              sourceId: item.calendarItem.id,
              targetId: chainAfter.targetId,
            },
          });
          setExistingChainGroupId(
            chainAfter.targetChainGroupId || chainAfter.targetId
          );
        } catch (chainErr) {
          console.error(
            "[EditEvent] joinCalendarChain failed:",
            chainErr?.message || chainErr
          );
          handleSnack(
            "Chain update failed — please retry.",
            3500
          );
        }
      }

      // Save splitBetween to the linked invoice/quote if values were provided.
      // Go By has no linked doc, so this block naturally skips when item.invoice
      // and item.quote are both null — but guard explicitly for clarity.
      if (eventType === "job") {
        const minVal = parseInt(splitMin);
        const maxVal = parseInt(splitMax);
        const newSplit = [];
        if (!isNaN(minVal)) newSplit.push(minVal);
        if (!isNaN(maxVal) && maxVal !== minVal) newSplit.push(maxVal);

        const doc = item.invoice || item.quote;
        const isInvoice = !!item.invoice;
        if (doc && newSplit.length > 0) {
          await updateQuoteInvoice({
            variables: {
              id: doc.id,
              isInvoice,
              discountPercent: doc.discountPercent || "0",
              subtotal: doc.subtotal || "0",
              total: isInvoice ? doc.invoiceTotal || "0" : doc.quoteTotal || "0",
              splitBetween: newSplit,
            },
          });
        }
      }

      /*
       * Available Sooner rides its own mutation, and only when it actually
       * changed — an unchanged checkbox should not write, broadcast a
       * calendar_update, or make every open Tasks console refetch.
       *
       * After the main update, so a failure here cannot roll back an
       * otherwise-good save; it surfaces as its own message instead.
       */
      let asapFailed = false;
      if (
        availableSooner !== availSoonerAtOpen &&
        ["job", "go_by"].includes(item?.calendarItem?.eventType || "job")
      ) {
        try {
          await setAvailSooner({
            variables: {
              itemId: item.calendarItem.id,
              availableSooner,
            },
          });
        } catch (asapErr) {
          asapFailed = true;
          console.error("Available Sooner save failed:", asapErr?.message || asapErr);
        }
      }

      /*
       * ONE snack, chosen — not two in a row.
       *
       * `handleSnack` writes a single state slot, so calling it twice in the
       * same tick simply overwrites: an error raised in the catch above was
       * replaced by "Event Updated" before React painted, and a failed flag
       * write was completely invisible while the user was told it saved.
       */
      handleSnack(
        asapFailed
          ? "Event saved, but Available Sooner did not update — retry from the event details."
          : "Event Updated",
        asapFailed ? 4000 : 2500
      );
      onEventUpdated?.();
    } catch (err) {
      // Moving an approved job across a settled pay period — confirm + retry.
      if (isNeedsPeriodConfirm(err)) {
        setPeriodMove({
          oldMs: item.calendarItem.startMs,
          newMs: startMs,
          onConfirm: () => handleSave(true),
        });
        setSubmitting(false);
        return;
      }
      console.error("Error updating calendar item:", err);
      const msg = err.message || "Failed to update event";
      setError(msg);
      handleSnack(msg, 3000);
    }

    setSubmitting(false);
  };

  // `scope` is a string or nothing. The typeof guard is the same one the
  // pay-period retry needs: wiring this straight to onClick would hand it a
  // React synthetic event, which is neither undefined nor a scope — it would
  // skip both the prompt and the confirm and send an event object to GraphQL.
  const handleDelete = async (rawScope) => {
    const scope = typeof rawScope === "string" ? rawScope : undefined;
    // An occurrence of a repeating series asks WHICH occurrences instead of
    // the usual click-twice confirm — the three answers differ far too much to
    // collapse into one "are you sure?".
    if (scope === undefined && isSeriesOccurrence(item)) {
      setScopePrompt({ mode: "delete" });
      return;
    }
    // No click-twice guard here any more: BdrButton owns the confirm for a
    // one-off delete (style guide §6b — one place owns destructive confirms,
    // so every delete reads and stacks the same). Leaving the old guard in
    // would mean confirming in the dialog and then having to click again.

    setDeleting(true);
    try {
      await deleteCalendarItem({
        variables: { id: item.calendarItem.id, scope: scope || null },
      });
      handleSnack("Event Deleted", 2500);
      onEventUpdated?.();
    } catch (err) {
      console.error("Error deleting calendar item:", err);
      const msg = err.message || "Failed to delete event";
      setError(msg);
      handleSnack(msg, 3000);
    }
    setDeleting(false);
  };

  // Personal ("SIX"): compact single-column dialog, no client / notes sidebar.
  const isPersonal = (item?.calendarItem?.eventType || "job") === "personal";

  return (
    <>
    <BdrModal
      open={open}
      onClose={onClose}
      variant="panel"
      size={isPersonal ? "md" : "lg"}
      showHeader={false}
      ariaLabel="Edit event"
      /* raised: opened from the day / week / day-view modals */
      layer="raised"
    >
      <div className="edit-event-content bdr-dark-placeholder">
        <div className="edit-event-header">
          <h3>{isPersonal ? "Edit Personal Event" : "Edit Calendar Event"}</h3>
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={`event-modal-layout${isPersonal ? " event-modal-layout--single" : ""}`}>
        <div className="event-modal-main">
        {/* Event Type (read-only — immutable per L10) */}
        <div className="edit-event-field">
          <span className="edit-event-field-label">Event Type</span>
          <div className="edit-event-readonly">
            {(() => {
              const t = item?.calendarItem?.eventType || "job";
              if (t === "go_by") return "Go By";
              if (t === "vacation") return "Time Off";
              if (t === "personal") return "Personal (SIX)";
              return "Job";
            })()}
          </div>
        </div>

        {/* Personal ("SIX") label + All Day switch */}
        {isPersonal && (
          <>
            <div className="edit-event-field">
              <BdrInput
                
                type="text"
                label="Label"
                name="label"
                onChange={(e) => setLabel(e.target.value)}
                value={label}
                maxLength={PERSONAL_LABEL_MAX}
              />
            </div>
            <div className="edit-event-field">
              <label className="edit-event-field-label">All Day</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Switch
                  checked={allDay}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setAllDay(on);
                    // Switching to all-day drops the end; the date is kept.
                    if (on) setEndMs("");
                  }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#00e676" },
                  }}
                />
                <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                  {allDay ? "All day — pick a date" : "Timed — pick a start and end"}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Client (read-only) — hidden for Vacation + Personal (no client) */}
        {!isPersonal && (item?.calendarItem?.eventType || "job") !== "vacation" && (
          <div className="edit-event-field">
            <span className="edit-event-field-label">Client</span>
            <div className="edit-event-readonly" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span>{getCustomerName()}</span>
              {(() => {
                const { day, fetchedMs } = weatherForJob(
                  buildWeatherLookup(zoneWeather),
                  clientZone,
                  startMs
                );
                return day ? (
                  <WeatherChip day={day} fetchedMs={fetchedMs} size={14} />
                ) : null;
              })()}
            </div>
          </div>
        )}

        {/* Invoice / Quote — read-only for real jobs; an attach dropdown for
            tentative placeholders so the office can upgrade them in place.
            Hidden for Go By (no document linked). */}
        {(item?.calendarItem?.eventType || "job") === "job" && (
          <div className="edit-event-field">
            <span className="edit-event-field-label">
              {item?.calendarItem?.isPlaceholder
                ? "Attach Invoice / Quote"
                : "Document"}
            </span>
            {item?.calendarItem?.isPlaceholder ? (
              /* `popup`: grows with the client's history, so a native list
                 can run off the screen. See BdrSelectPopup.jsx. */
              <BdrInput
                name="attachDoc"
                popup
                placeholder="-- Keep as placeholder --"
                options={docOptions}
                value={
                  selectedDocId ? `${selectedDocType}:${selectedDocId}` : ""
                }
                onChange={handleDocChange}
              />
            ) : (
              <div className="edit-event-readonly">{getDocLabel()}</div>
            )}
          </div>
        )}

        {isPersonal && allDay ? (
          /* All-day personal: date only; submit re-anchors to local noon. */
          <div className="edit-event-field">
            <div className="edit-event-row">
              <EventDateTimeInput
                value={startMs}
                onChange={(ms) => {
                  if (typeof ms === "string") setStartMs(ms);
                }}
                label="Date:"
                hideTime
                defaultTime={{ hour24: 12, minute: 0 }}
              />
            </div>
          </div>
        ) : (
          <>
            {/* Start Time */}
            <div className="edit-event-field">
              <div className="edit-event-row">
                <EventDateTimeInput
                  value={startMs}
                  onChange={handleStartTimeReturn}
                  label="Start:"
                />
              </div>
            </div>

            {/* End Time */}
            <div className="edit-event-field">
              <div className="edit-event-row">
                <EventDateTimeInput
                  value={endMs}
                  onChange={handleEndTimeReturn}
                  label="End:"
                />
              </div>
            </div>
          </>
        )}

        {/* Available Sooner — own row under the date fields, job / go_by only.
            Written through its OWN mutation on save (see below), not through
            UPDATE_CALENDAR_ITEM: the field is deliberately absent from that
            input type, which is what keeps the general update path closed. */}
        {["job", "go_by"].includes(item?.calendarItem?.eventType || "job") && (
          <div className="edit-event-row">
            <BdrCheckbox
              name="availableSooner"
              label="Available Sooner"
              helper="Lists this customer on the Tasks page so an earlier opening can be offered to them."
              checked={availableSooner}
              onChange={(e) => setAvailableSooner(e.target.checked)}
            />
          </div>
        )}

        {/* Repeat rule (2026-09-20). Rendered after the date fields because the
            pattern day is DERIVED from the event's own date. A rule whose
            schedule has become impossible renders as a one-off on its anchor
            day, so say so rather than showing controls that describe nothing. */}
        {isPersonal && (
          <>
            {item?.calendarItem?.recurrenceInvalid && (
              <div
                className="edit-event-field"
                style={{ color: "#ffe082", fontSize: "0.82rem" }}
              >
                This repeating event has an invalid schedule and is showing as a
                single event. Set the repeat again to fix it.
              </div>
            )}
            <RepeatFields
              value={repeat}
              onChange={setRepeat}
              dateMs={startMs}
              disabled={submitting}
              /* While the date is still the tapped occurrence's, the preview
                 shows the STORED day-of-month — a snapped Feb 28 on a "31st"
                 series previews "31st", which is what the save keeps (D11).
                 Pick another date and the preview follows it. (2026-09-25) */
              storedMonthDay={
                isSameLocalDay(startMs, item?.calendarItem?.startMs)
                  ? item?.calendarItem?.recurrence?.byMonthDay
                  : null
              }
            />
          </>
        )}

        {/* Is After chain selector — hidden for Vacation (§6.12) + Personal */}
        {!isPersonal && (item?.calendarItem?.eventType || "job") !== "vacation" && startMs && (
          <ChainSelector
            dayStartMs={startMs}
            currentEventId={item?.calendarItem?.id || null}
            chainGroupId={existingChainGroupId}
            selectedAfterId={chainAfter?.targetId || null}
            onChange={setChainAfter}
            onBreakChain={async () => {
              const id = item?.calendarItem?.id;
              if (!id) return;
              try {
                await breakCalendarChain({ variables: { id } });
                setExistingChainGroupId(null);
                setChainAfter(null);
                handleSnack("Chain broken", 2000);
              } catch (err) {
                console.error("[EditEvent] breakCalendarChain failed:", err?.message || err);
                handleSnack(err?.message || "Failed to break chain", 3000);
              }
            }}
            eventType={item?.calendarItem?.eventType || "job"}
          />
        )}

        {/* Work Days — only shown when end date > start date (never for personal) */}
        {!isPersonal && workDayList.length > 0 && (
          <div className="edit-event-field">
            <span className="edit-event-field-label">
              Work Days ({Array.from(checkedKeys).length} selected)
            </span>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "8px 10px",
                background: "rgba(255,255,255,0.05)",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <div style={{ fontSize: 11, color: "#b0b0cc", marginBottom: 2 }}>
                Weekends auto-skipped. Middle days run 8 AM – 6 PM. First/last day use your chosen start/end times.
              </div>
              {(() => {
                const checkedInOrder = workDayList.filter((d) => checkedKeys.has(d.key));
                return workDayList.map((d) => {
                  const isChecked = checkedKeys.has(d.key);
                  const idxInChecked = checkedInOrder.findIndex((e) => e.key === d.key);
                  const isFirst = isChecked && idxInChecked === 0;
                  const isLast = isChecked && idxInChecked === checkedInOrder.length - 1;
                  const dateLabel = `${WEEKDAY_LABELS[d.weekday]} ${d.month + 1}/${d.day}`;
                  const timeLabel = isChecked
                    ? previewDayTimes(isFirst, isLast, startMs, endMs)
                    : "skipped";
                  let roleLabel = "";
                  if (isChecked) {
                    if (checkedInOrder.length === 1) roleLabel = "[only day]";
                    else if (isFirst) roleLabel = "[first]";
                    else if (isLast) roleLabel = "[last]";
                  }
                  return (
                    <BdrCheckbox
                      key={d.key}
                      className={`edit-event-day-row${
                        isChecked ? "" : " is-off"
                      }`}
                      checked={isChecked}
                      onChange={(e) => {
                        setCheckedKeys((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(d.key);
                          else next.delete(d.key);
                          return next;
                        });
                      }}
                      label={
                        <>
                          <span className="edit-event-day-date">{dateLabel}</span>
                          <span className="edit-event-day-time">{timeLabel}</span>
                          {roleLabel && (
                            <span className="edit-event-day-role">{roleLabel}</span>
                          )}
                        </>
                      }
                    />
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Assigned Users — hidden for Personal (creator-stamped, immutable) */}
        {!isPersonal && (
        <div className="edit-event-field">
          <span className="edit-event-field-label">Assigned To</span>
          <div className="edit-event-user-list">
            {users.map((user) => (
              <BdrCheckbox
                key={user._id}
                className="edit-event-user-row"
                checked={assignedUserIds.includes(user._id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setAssignedUserIds((prev) => [...prev, user._id]);
                  } else {
                    setAssignedUserIds((prev) =>
                      prev.filter((id) => id !== user._id)
                    );
                  }
                }}
                label={
                  <>
                    <span
                      className="edit-event-user-dot"
                      style={{ background: user.defaultColor || "#888" }}
                    />
                    <span className="edit-event-user-name">
                      {user.firstName} {user.lastName}
                    </span>
                  </>
                }
              />
            ))}
          </div>
        </div>
        )}

        {/* Priority Notes — hidden for Vacation + Personal */}
        {!isPersonal && (item?.calendarItem?.eventType || "job") !== "vacation" && (
          <div className="edit-event-field">
            <BdrInput
              
              type="text"
              label="Priority Notes"
              name="priorityNote"
              onChange={(e) => setPriorityNote(e.target.value)}
              value={priorityNote}
              multiline
              rows={2}
            />
          </div>
        )}

        {/* Go By / Vacation / Personal Note (editable) */}
        {(() => {
          const t = item?.calendarItem?.eventType || "job";
          if (t !== "go_by" && t !== "vacation" && t !== "personal") return null;
          return (
            <div className="edit-event-field">
              <BdrInput
                
                type="text"
                label={
                  t === "go_by"
                    ? "Go By Note (optional)"
                    : t === "personal"
                      ? "Note (optional)"
                      : "Time Off Note (optional)"
                }
                name="note"
                onChange={(e) => setNote(e.target.value)}
                value={note}
                multiline
                rows={2}
              />
            </div>
          );
        })()}

        {/* Item Status — hidden for Go By (no job-form lifecycle) */}
        {(item?.calendarItem?.eventType || "job") === "job" && (
          <div className="edit-event-field">
            <label className="edit-event-field-label">Status</label>
            <BdrSelectPopup
              id="edit-event-item-status"
              value={itemStatus}
              onChange={(e) => setItemStatus(e.target.value)}
              className="bdr-input bdr-select-trigger"
              options={[
                { value: "", label: "Not Started" },
                { value: "PENDING", label: "Pending" },
                { value: "APPROVED", label: "Approved" },
              ]}
            />
          </div>
        )}

        {/* Refinisher Min/Max — every job, placeholders included (2026-10-29:
            a placeholder keeps the number on the calendar item until it is
            promoted). Hidden for Go By / Vacation / Personal. */}
        {(item?.calendarItem?.eventType || "job") === "job" && (
          <div className="edit-event-field">
            <span className="edit-event-field-label">Refinishers (Min / Max)</span>
            <div style={{ display: "flex", gap: 14 }}>
              <NumberStepper
                label="Min"
                value={splitMin}
                onChange={(val) => setSplitMin(String(val))}
                min={1}
                max={99}
              />
              <NumberStepper
                label="Max"
                value={splitMax}
                onChange={(val) => setSplitMax(String(val))}
                min={1}
                max={99}
              />
            </div>
          </div>
        )}

        {/* Lock Job — hidden for Vacation (no AI reassignment to lock against) */}
        {!isPersonal && (item?.calendarItem?.eventType || "job") !== "vacation" && (
          <div className="edit-event-field">
            <label className="edit-event-field-label">Lock Job</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={locked}
                onChange={(e) => setLocked(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#00e676" },
                }}
              />
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                {locked ? "Locked — AI will not reassign" : "Unlocked"}
              </span>
            </div>
          </div>
        )}

        {/* Special Refinisher Pay — real job events only (not placeholders) */}
        {(item?.calendarItem?.eventType || "job") === "job" &&
          !item?.calendarItem?.isPlaceholder && (
          <div className="edit-event-field">
            <SpecialRefinisherPayBlock
              enabled={srpEnabled}
              setEnabled={(v) => {
                srpDirtyRef.current = true;
                setSrpEnabled(v);
              }}
              amount={srpAmount}
              setAmount={(v) => {
                srpDirtyRef.current = true;
                setSrpAmount(v);
              }}
              payType={srpPayType}
              setPayType={(v) => {
                srpDirtyRef.current = true;
                setSrpPayType(v);
              }}
              theme="dark"
              items={item?.invoice?.invoiceItems || item?.quote?.quoteItems || []}
            />
          </div>
        )}

        {/* Error */}
        {error && <div className="edit-event-error">{error}</div>}

        {/* Actions */}
        <div className="edit-event-actions">
          <BdrButton
            variant="danger"
            /* A repeating occurrence asks WHICH occurrences instead, via
               RepeatScopeModal — the three answers differ far too much to
               collapse into "are you sure?". Suppressing the shell's confirm
               there is what stops the user being asked twice. */
            confirm={!isSeriesOccurrence(item)}
            note="This permanently deletes the event."
            confirmTitle="Delete this event?"
            confirmLabel="Delete"
            /* top: this modal is itself on the raised rung (§6b). */
            confirmLayer="top"
            onClick={() => handleDelete()}
            disabled={deleting || submitting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </BdrButton>
          <div className="edit-event-actions-right">
            <BdrButton onClick={onClose}>Cancel</BdrButton>
            {/* solid: this modal is variant="panel" (§6b). */}
            <BdrButton
              variant="confirm-solid"
              onClick={handleSave}
              disabled={submitting || deleting}
            >
              {submitting ? "Saving..." : "Save Changes"}
            </BdrButton>
          </div>
        </div>
        </div>{/* end event-modal-main */}

        {/* Notes Sidebar — hidden for Personal (no client, no cal notes) */}
        {!isPersonal && (
        <div className="event-modal-notes-sidebar">
          {/* Cal Notes */}
          <div className="event-notes-cal-section">
            <h4 className="event-notes-cal-title">Cal Notes</h4>
            {gateCode && gateCode.trim() !== "" && (
              <div className="event-notes-cal-item" style={{ userSelect: "text" }}>
                Gate Code: {gateCode}
              </div>
            )}
            {(() => {
              const calNotes = extractCalNotes(clientNotes);
              return calNotes.length > 0 ? (
                <div className="event-notes-cal-list">
                  {calNotes.map((note, idx) => (
                    <div key={idx} className="event-notes-cal-item" style={{ userSelect: "text" }}>
                      {note}
                    </div>
                  ))}
                </div>
              ) : (
                <span className="event-notes-muted">No calendar notes</span>
              );
            })()}
          </div>

          <div className="event-notes-divider" />

          {/* Client Notes */}
          <div className="event-notes-list">
            {clientNotes.length > 0 ? (
              clientNotes.map((n, idx) => (
                <div key={n._id || idx} className="event-note-item">
                  <div className="event-note-date">
                    {BAGroupTimeFormat(n.dateCreated, true, true, true)}
                  </div>
                  <div className="event-note-body" style={{ userSelect: "text" }}>
                    {n.note}
                  </div>
                </div>
              ))
            ) : (
              <span className="event-notes-muted">No client notes</span>
            )}
          </div>
        </div>
        )}
        </div>{/* end event-modal-layout */}
      </div>
    </BdrModal>
    <PlaceholderCleanupModal
      open={cleanupOpen}
      clientId={item?.calendarItem?.clientId}
      excludeEventId={item?.calendarItem?.id}
      onClose={() => {
        setCleanupOpen(false);
        onEventUpdated?.();
      }}
    />
    <BdrModal
      open={!!promoteConfirm}
      onClose={() => {
        setPromoteConfirm(null);
        setSubmitting(false);
      }}
      variant="panel"
      size="md"
      showHeader={false}
      ariaLabel="Move existing booking here?"
      /* top: opened from this modal, which is itself raised */
      layer="top"
    >
      {promoteConfirm && (
        <div style={{ padding: 20 }} className="bdr-dark-placeholder">
          <h3 style={{ marginTop: 0 }}>Move existing booking here?</h3>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" }}>
            {promoteConfirm.docLabel} is already on the calendar on{" "}
            <b>{BAGroupTimeFormat(promoteConfirm.existingStartMs, true, true)}</b>.
            Promoting this tentative event will <b>move that booking</b> to{" "}
            <b>{BAGroupTimeFormat(promoteConfirm.placeholderStartMs, true, true)}</b>{" "}
            and remove this tentative placeholder. The existing job's invoice,
            job form, and pay stay attached to it.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 16,
            }}
          >
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                background: "rgba(255,255,255,0.18)",
              }}
              onClick={() => {
                setPromoteConfirm(null);
                setSubmitting(false);
              }}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                background: "#e53935",
                opacity: submitting ? 0.6 : 1,
              }}
              onClick={() => {
                setSubmitting(true);
                runPromote(true);
              }}
              disabled={submitting}
            >
              Move &amp; Promote
            </button>
          </div>
        </div>
      )}
    </BdrModal>
    <RepeatScopeModal
      open={!!scopePrompt}
      mode={scopePrompt?.mode || "edit"}
      /* The day on screen (startMs), never the pattern slot — see the modal. */
      displayDayMs={item?.calendarItem?.startMs}
      splitAtMs={item?.calendarItem?.splitAtMs}
      truncatedAtMs={item?.calendarItem?.truncatedAtMs}
      overrideCount={overrideCount(item)}
      warning={scopePrompt?.mode === "delete" ? "" : repeatSaveWarning(item, repeat)}
      busy={submitting}
      onCancel={() => setScopePrompt(null)}
      onChoose={async (scope) => {
        const mode = scopePrompt?.mode;
        setScopePrompt(null);
        if (mode === "delete") await handleDelete(scope);
        else await handleSavePersonal(scope);
      }}
    />
    <PayPeriodMoveConfirmModal
      open={!!periodMove}
      oldMs={periodMove?.oldMs}
      newMs={periodMove?.newMs}
      busy={submitting}
      onCancel={() => {
        setPeriodMove(null);
        setSubmitting(false);
      }}
      onConfirm={() => {
        setSubmitting(true);
        periodMove?.onConfirm?.();
      }}
    />
    </>
  );
};

export default EditEventModal;
